// frontend/src/pages/IndustryTemplatesPage.tsx
// Admin UI for browsing and applying industry templates to the organization

import React, { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

interface IndustryTemplate {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  industry: string;
  version: { major: number; minor: number; patch: number };
  status: 'draft' | 'published' | 'deprecated';
  projectFields: any[];
  correspondenceFields: any[];
  createdAt: string;
  updatedAt: string;
}

const INDUSTRY_ICONS: Record<string, string> = {
  construction: '🏗️',
  legal: '⚖️',
  finance: '💰',
  healthcare: '🏥',
  manufacturing: '🏭',
  real_estate: '🏠',
  technology: '💻',
  government: '🏛️',
  education: '🎓',
  retail: '🛒',
  logistics: '🚛',
  energy: '⚡',
  telecommunications: '📡',
  insurance: '🛡️',
  consulting: '📊',
  other: '📄',
};

export const IndustryTemplatesPage: React.FC = () => {
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();

  const [templates, setTemplates] = useState<IndustryTemplate[]>([]);
  const [industries, setIndustries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<IndustryTemplate | null>(null);
  const [applying, setApplying] = useState(false);
  const [currentCustomization, setCurrentCustomization] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (selectedIndustry) params.industry = selectedIndustry;

      const [templatesResult, industriesResult, custResult] = await Promise.all([
        apiClient.industryTemplates.list(params),
        apiClient.industryTemplates.getIndustries(),
        apiClient.organizationCustomization.getCustomization().catch(() => null),
      ]);

      setTemplates((templatesResult as any)?.data || []);
      setIndustries((industriesResult as any)?.data || []);
      setCurrentCustomization((custResult as any)?.data || custResult);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [selectedIndustry]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyTemplate = async (template: IndustryTemplate) => {
    const confirm = window.confirm(
      `Apply "${template.name}" template to your organization?\n\nThis will set up ${template.correspondenceFields?.length || 0} correspondence fields and ${template.projectFields?.length || 0} project fields.`
    );
    if (!confirm) return;

    setApplying(true);
    try {
      if (currentCustomization) {
        // Update existing customization with new template
        await apiClient.organizationCustomization.updateCustomization({
          baseTemplate: template._id,
        });
      } else {
        // Create new customization
        await apiClient.organizationCustomization.createCustomization({
          baseTemplate: template._id,
        });
      }
      addNotification?.({
        type: 'success',
        title: 'Template Applied',
        message: `"${template.name}" template has been applied to your organization.`,
      });
      fetchData();
    } catch (err: any) {
      addNotification?.({
        type: 'error',
        title: 'Failed to Apply',
        message: err?.response?.data?.error || err.message,
      });
    } finally {
      setApplying(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'implementor')) {
    return (
      <PageLayout title="Industry Templates" subtitle="Access denied">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Only administrators can access industry templates.</p>
        </div>
      </PageLayout>
    );
  }

  const currentTemplateId = currentCustomization?.baseTemplate?._id || currentCustomization?.baseTemplate;

  return (
    <PageLayout title="Industry Templates" subtitle="Browse and apply pre-configured field templates for your industry">
      <div className="space-y-6">
        {error && <ErrorAlert message={error} onClose={() => setError(null)} />}

        {/* Current Template Banner */}
        {currentCustomization?.baseTemplate && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">✅</span>
              <div>
                <p className="font-medium text-green-900 dark:text-green-100">
                  Current Template: {typeof currentCustomization.baseTemplate === 'object' ? currentCustomization.baseTemplate.name : 'Applied'}
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Version: {typeof currentCustomization.templateVersionAtSelection === 'object' && currentCustomization.templateVersionAtSelection
                    ? `${currentCustomization.templateVersionAtSelection.major || 0}.${currentCustomization.templateVersionAtSelection.minor || 0}.${currentCustomization.templateVersionAtSelection.patch || 0}`
                    : (currentCustomization.templateVersionAtSelection || 'N/A')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Industry Filter */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Industry:</label>
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="">All Industries</option>
            {industries.map((ind: any) => (
              <option key={ind.value || ind.industry || ind} value={ind.value || ind.industry || ind}>
                {ind.label || String(ind.value || ind.industry || ind).replace(/_/g, ' ')} ({ind.templateCount || ind.count || 0})
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No templates available</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {selectedIndustry ? 'Try a different industry filter.' : 'No published templates exist yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => {
              const isApplied = currentTemplateId === template._id;
              return (
                <div
                  key={template._id}
                  className={cn(
                    'bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-5 transition-all hover:shadow-md',
                    isApplied
                      ? 'border-green-300 dark:border-green-700 ring-1 ring-green-200 dark:ring-green-800'
                      : 'border-gray-200 dark:border-gray-700'
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{INDUSTRY_ICONS[template.industry] || '📄'}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{String(template.industry || '').replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    {isApplied && (
                      <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full font-medium">
                        Active
                      </span>
                    )}
                  </div>

                  {template.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{template.description}</p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <span>{template.correspondenceFields?.length || 0} correspondence fields</span>
                    <span>{template.projectFields?.length || 0} project fields</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      v{template.version?.major || 1}.{template.version?.minor || 0}.{template.version?.patch || 0}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedTemplate(selectedTemplate?._id === template._id ? null : template)}
                      >
                        {selectedTemplate?._id === template._id ? 'Hide Details' : 'View Details'}
                      </Button>
                      {!isApplied && (
                        <Button
                          variant="primary"
                          onClick={() => handleApplyTemplate(template)}
                          disabled={applying}
                        >
                          {applying ? 'Applying...' : 'Apply'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Template Details Panel */}
        {selectedTemplate && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedTemplate.name} — Field Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Correspondence Fields */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Correspondence Fields ({selectedTemplate.correspondenceFields?.length || 0})
                </h4>
                {(selectedTemplate.correspondenceFields || []).length === 0 ? (
                  <p className="text-sm text-gray-400">None</p>
                ) : (
                  <ul className="space-y-1">
                    {selectedTemplate.correspondenceFields.map((f: any, i: number) => (
                      <li key={i} className="text-sm flex items-center gap-2 py-1">
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{f.type}</span>
                        <span className="text-gray-900 dark:text-white">{f.name}</span>
                        {f.required && <span className="text-red-500 text-xs">*</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Project Fields */}
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Fields ({selectedTemplate.projectFields?.length || 0})
                </h4>
                {(selectedTemplate.projectFields || []).length === 0 ? (
                  <p className="text-sm text-gray-400">None</p>
                ) : (
                  <ul className="space-y-1">
                    {selectedTemplate.projectFields.map((f: any, i: number) => (
                      <li key={i} className="text-sm flex items-center gap-2 py-1">
                        <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{f.type}</span>
                        <span className="text-gray-900 dark:text-white">{f.name}</span>
                        {f.required && <span className="text-red-500 text-xs">*</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
